import { useContext, useMemo, useCallback } from "react"
import { SpaceDashboardOutlined } from "@mui/icons-material"
import { useSelector } from "react-redux"
import { getAiTermsAccepted } from "modules/account-root/selectors"
import HomeHeader from "modules/home-header"
import InstrumentSelector from "connected-components/instrument-selector"
import ContextualMenu from "components/contextual-menu"
import { useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import { usePositionSetMetadata } from "resources/position-sets"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import AiToggleButton from "connected-components/ai-teammate/components/ai-toggle-button"
import { LOADING } from "helpers/data-statuses"
import { DashboardContext } from "./context/dashboard"
import { useResourceMenuItems } from "./hooks/use-resource-menu-items"
import useOpenUnifiedSearch from "./hooks/use-open-unified-search"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "./analytics"

export default function DashboardHeader() {
  const accountAlias = useAccountAlias()
  const dashboardContext = useContext(DashboardContext)
  const analytics = useAnalytics()
  const { instrument, modelId, setInstrument } = dashboardContext
  const isAiTermsAccepted = useSelector(getAiTermsAccepted)

  const [modelMetadata] = useModelMetadata(modelId)
  const [instrumentMetadata] = usePositionSetMetadata(modelId, instrument, modelMetadata?.availability?.currentDate)
  const resourceMenuCallbacks = useMemo(() => ({
    onSuccess: () => window.location.reload(),
    onDelete: () => setInstrument(null),
  }), [setInstrument])

  const [resourceMenuItems, resourceMenuItemsStatus] = useResourceMenuItems(instrument, resourceMenuCallbacks)

  const isPortfolioInstrument = instrument?.type === "PORTFOLIO"

  const canOpenAiDrawer = isAiTermsAccepted && !!instrument && !!modelId
  const onHeaderInstrumentSelection = useCallback(({ id, name, type, searchType, isInitialSelection }) => {
    const payload = { instrument: { id, name, type }, searchType }
    if (isInitialSelection) {
      analytics.track(DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION, payload)
    } else {
      analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, payload)
    }
  }, [analytics])

  const openSearch = useOpenUnifiedSearch(DashboardContext, setInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, null, true, false, onHeaderInstrumentSelection)

  return <HomeHeader Icon={SpaceDashboardOutlined} title="Dashboard" account={accountAlias} className="dashboard-header" modelId={modelId}>
    {isPortfolioInstrument && resourceMenuItems && <ContextualMenu className="dashboard-contextual-menu" items={resourceMenuItems} disabled={resourceMenuItemsStatus === LOADING} />}
    {instrumentMetadata && <InstrumentSelector context={DashboardContext} openSearch={openSearch} selectedInstrumentMetadata={instrumentMetadata} onClear={() => { setInstrument(null) }} size="medium" />}
    <div className="spacer" />
    {canOpenAiDrawer && <AiToggleButton context={DashboardContext} />}
  </HomeHeader>
}
